import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

// Public
import Landing from "@/pages/Landing";
import Features from "@/pages/Features";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Accessibility from "@/pages/Accessibility";
import Invest from "@/pages/Invest";
import PublicRoadmap from "@/pages/PublicRoadmap";
import FundAFeature from "@/pages/FundAFeature";
import FundAFeatureTerms from "@/pages/FundAFeatureTerms";
import BetaSignup from "@/pages/BetaSignup";
import Feedback from "@/pages/Feedback";
import PublicBooking from "@/pages/PublicBooking";
import PublicBookingV2 from "@/pages/PublicBookingV2";
import BookingManage from "@/pages/BookingManage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCanceled from "@/pages/PaymentCanceled";
import Lobby from "@/pages/Lobby";
import MeetingRoom from "@/pages/MeetingRoom";
import BookingConfirmation from "@/pages/BookingConfirmation";
import GuestThanks from "@/pages/GuestThanks";
import MeetingUnavailable from "@/pages/MeetingUnavailable";
import NotFound from "@/pages/NotFound";

// Auth
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import BetaDisclaimer from "@/pages/BetaDisclaimer";
import AuthCallback from "@/pages/AuthCallback";

// App
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Meetings from "@/pages/Meetings";
import CreateMeeting from "@/pages/CreateMeeting";
import MeetingDetails from "@/pages/MeetingDetails";
import Bookings from "@/pages/Bookings";
import BookingsHub from "@/pages/BookingsHub";
import Contacts from "@/pages/Contacts";
import Files from "@/pages/Files";
import Recall from "@/pages/Recall";
import Tasks from "@/pages/Tasks";
import Notes from "@/pages/Notes";
import Roadmap from "@/pages/Roadmap";
import AdminRoute from "@/components/AdminRoute";
import MarketingSettings from "@/pages/MarketingSettings";
import Settings from "@/pages/Settings";
import EmailAdmin from "@/pages/EmailAdmin";
import TaskTemplates from "@/pages/TaskTemplates";
import GlobalSearch from "@/pages/GlobalSearch";
import AdminSweep from "@/pages/AdminSweep";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";

function RouterShell() {
  // Handle Emergent OAuth callback synchronously, before any other routing logic.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Marketing */}
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/roadmap" element={<PublicRoadmap />} />
      <Route path="/fund-a-feature" element={<FundAFeature />} />
      <Route path="/fund-a-feature-terms" element={<FundAFeatureTerms />} />
      <Route path="/invest" element={<Invest />} />
      <Route path="/beta-signup" element={<BetaSignup />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/accessibility" element={<Accessibility />} />

      {/* Public booking + lobby */}
      <Route path="/book/:slug" element={<PublicBookingV2 />} />
      <Route path="/a/:slug" element={<PublicBookingV2 mode="appointment" />} />
      <Route path="/book-legacy/:slug" element={<PublicBooking />} />
      <Route path="/booking-manage/:token" element={<BookingManage />} />
      <Route path="/booking/payment/success" element={<PaymentSuccess />} />
      <Route path="/booking/payment/canceled" element={<PaymentCanceled />} />
      <Route path="/booking-confirmed" element={<BookingConfirmation />} />
      <Route path="/m/:publicId" element={<Lobby />} />
      <Route path="/m/:publicId/room" element={<MeetingRoom />} />
      <Route path="/m/:publicId/thanks" element={<GuestThanks />} />
      <Route path="/m/:publicId/unavailable" element={<MeetingUnavailable />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/beta-disclaimer" element={<BetaDisclaimer />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/forgot" element={<ForgotPassword />} />
      <Route path="/auth/reset/:token" element={<ResetPassword />} />
      <Route path="/auth/verify/:token" element={<VerifyEmail />} />

      {/* App (protected) */}
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <AppShellRoutes />
          </ProtectedRoute>
        }
      />

      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AppShellRoutes() {
  return (
    <AppShell>
      <Routes>
        <Route path="" element={<Dashboard />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="meetings" element={<Meetings />} />
        <Route path="meetings/new" element={<CreateMeeting />} />
        <Route path="meetings/:meetingId" element={<MeetingDetails />} />
        <Route path="bookings" element={<BookingsHub />} />
        <Route path="bookings/legacy" element={<Bookings />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="files" element={<Files />} />
        <Route path="summaries" element={<Recall />} />
        <Route path="recall" element={<Recall />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="tasks/templates" element={<TaskTemplates />} />
        <Route path="tasks/completed" element={<Tasks />} />
        <Route path="notes" element={<Notes />} />
        <Route path="search" element={<GlobalSearch />} />
        <Route path="roadmap" element={<AdminRoute><Roadmap /></AdminRoute>} />
        <Route path="settings/marketing" element={<AdminRoute><MarketingSettings /></AdminRoute>} />
        <Route path="admin/users" element={<AdminRoute><AdminSweep /></AdminRoute>} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/email" element={<EmailAdmin />} />
        {/* /app/billing route removed — contents merged into /app/settings?tab=billing.
            Old direct hits redirect to the Settings → Billing tab. */}
        <Route path="billing" element={<Navigate to="/app/settings?tab=billing" replace />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          <RouterShell />
          <Toaster position="bottom-right" richColors closeButton />
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
